import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootStore } from 'store/types';
import { IInternalTransferData } from 'types/order/escrow/IInternalTransferData';
import {
  deleteInternalTransferAction,
  deleteInternalTransferSlice,
} from 'store/order/escrow/disbursements/internalTransfers/deleteInternalTransferSlice';
import { sendInternalTransferAction } from 'store/order/escrow/disbursements/internalTransfers/sendInternalTransferSlice';
import { getDestinationAccountsForStateAction } from 'store/order/escrow/disbursements/internalTransfers/getInternalTransferSlice';
import currencyJs from 'currency.js';
import {
  convertIInternalTransferDataToIInternalTransferPatchData,
  IInternalTransferPatchData,
} from 'types/order/escrow/state/internalTransfers/IInternalTransferPatchData';
import { useAppDispatch } from '../../../../store/store';

interface UseCreateEditInternalTransferProps {
  orderId: string;
  availableAmountDollars: number;
  onTransactionUpdate: () => void;
}

export const useCreateEditInternalTransfer = ({
  orderId,
  availableAmountDollars,
  onTransactionUpdate,
}: UseCreateEditInternalTransferProps) => {
  const dispatch = useAppDispatch();
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [sendInternalTransferId, setSendInternalTransferId] = useState<string | undefined>(undefined);

  const {
    sendInternalTransfer: { fetchStatus: sendFetchStatus, fetchError: sendFetchError },
    getDestinationAccountsForState,
    deleteInternalTransfer: { fetchStatus: deleteFetchStatus },
    updateInternalTransfer: { fetchStatus: updateFetchStatus },
  } = useSelector((store: RootStore) => store.order.escrow.disbursements);

  useEffect(() => {
    dispatch(getDestinationAccountsForStateAction(orderId));
  }, [dispatch, orderId]);

  useEffect(() => {
    if (deleteFetchStatus.success) {
      onTransactionUpdate();
      dispatch(deleteInternalTransferSlice.actions.reset({}));
    }
  }, [dispatch, onTransactionUpdate, deleteFetchStatus.success]);

  const onRemoveInternalTransfer = useCallback(
    async (internalTransferToRemove: IInternalTransferData) => {
      if (internalTransferToRemove.createdAt) {
        dispatch(deleteInternalTransferAction(internalTransferToRemove.id));
      } else {
        dispatch(deleteInternalTransferSlice.actions.setData({}));
      }
    },
    [dispatch]
  );

  const sendInternalTransfer = useCallback(
    async (internalTransfer: IInternalTransferData) => {
      const patchData: IInternalTransferPatchData =
        convertIInternalTransferDataToIInternalTransferPatchData(internalTransfer);
      setSendInternalTransferId(patchData.id);
      setShowWarningModal(false);
      await dispatch(sendInternalTransferAction(patchData));
      setSendInternalTransferId(undefined);
      onTransactionUpdate();
    },
    [dispatch, onTransactionUpdate]
  );

  const onSendInternalTransfer = useCallback(
    async (internalTransfer: IInternalTransferData) => {
      if (internalTransfer.amountCents > currencyJs(availableAmountDollars).multiply(100).value) {
        return setShowWarningModal(true);
      } else {
        await sendInternalTransfer(internalTransfer);
      }
    },
    [availableAmountDollars, sendInternalTransfer]
  );

  return {
    showWarningModal,
    setShowWarningModal,
    sendInternalTransferId,
    sendFetchStatus,
    sendFetchError,
    updateFetchStatus,
    getDestinationAccountsForState,
    onRemoveInternalTransfer,
    onSendInternalTransfer,
  };
};
